import { useMemo } from 'react';
import { Avatar } from '@elevator/ui';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import useStyles from './styles';
import { SearchAuthorResult } from '@elevator/ui';

/**
 * AuthorsSelect component displays a dropdown/select for selecting a preferred writer/author.
 * Used in submission forms for sponsored content to assign a preferred writer.
 * Shows author avatars and display names in the dropdown with an "Any Writer" option.
 *
 * @param props - Component props
 * @param props.authors - Array of author results containing preferredUsername, displayName, and avatar
 * @param props.onChange - Callback function called when selection changes, receives preferredUsername
 * @param props.value - Current selected author's preferredUsername (optional)
 * @returns AuthorsSelect component
 */
const AuthorsSelect = (props: AuthorsSelectPropTypes) => {
  const { classes } = useStyles();
  const { authors, onChange, value } = props;

  const handleAuthorChange = (event: SelectChangeEvent<string>) => {
    onChange(event.target.value);
  };

  const selectedAuthor = useMemo(() => {
    if (!value) return null;
    return authors.find((author) => author.preferredUsername === value) || null;
  }, [authors, value]);

  const renderValue = (preferredUsername: string) => {
    if (!preferredUsername) {
      return <span className={classes.anyWriterValue}>Any Writer</span>;
    }

    if (!selectedAuthor) return '';

    const { displayName, avatar } = selectedAuthor;

    return (
      <span className={classes.selectedWriter}>
        <span className={classes.avatarWrapper}>
          <Avatar
            src={avatar?.sourceUrl}
            srcSet={avatar?.sourceSet}
            username={preferredUsername}
            size="small"
          />
        </span>
        {displayName || preferredUsername}
      </span>
    );
  };

  return (
    <FormControl variant="filled" fullWidth data-component="AuthorsSelect">
      <InputLabel id="preferredWriterLabel" shrink className={classes.inputLabelRoot}>
        Preferred Writer
      </InputLabel>
      <Select
        id="preferredWriter"
        name="preferredWriter"
        labelId="preferredWriterLabel"
        label="Preferred Writer"
        fullWidth
        variant="filled"
        className={classes.root}
        classes={{
          root: classes.selectRoot,
          select: classes.selectInput,
          icon: classes.dropdownArrow,
        }}
        value={value || ''}
        onChange={handleAuthorChange}
        displayEmpty
        MenuProps={{
          classes: {
            paper: classes.dropdownContainer,
          },
        }}
        renderValue={renderValue}
      >
        <MenuItem key="any-writer" value="">
          <ListItemText primary="Any Writer" />
        </MenuItem>
        {authors.map(({ preferredUsername, displayName, avatar }) => (
          <MenuItem key={preferredUsername} value={preferredUsername}>
            <ListItemAvatar className={classes.listItemAvatar}>
              <Avatar
                src={avatar?.sourceUrl}
                srcSet={avatar?.sourceSet}
                username={preferredUsername}
                size="small"
              />
            </ListItemAvatar>
            <ListItemText primary={displayName || preferredUsername} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export interface AuthorsSelectPropTypes {
  /** Array of author results containing preferredUsername, displayName, and avatar */
  authors: SearchAuthorResult[];
  /** Callback function called when selection changes, receives preferredUsername */
  onChange: (preferredUsername: string) => void;
  /** Current selected author's preferredUsername (optional) */
  value?: string;
}

export default AuthorsSelect;
